// MUI
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// Libraries
import { useState } from "react";
import * as d3 from 'd3';

// Types
import { State, County, ChangeYear, EquityIndicator, Indicator } from '../utils/Types';

// Globals
import { defaultCounty, appTitle, IndicatorLabel } from "../utils/Global";

// Components
import { InfoIconListener } from './Tooltip';
import { handleClickState, handleClickCounty, handleClickYear, 
         handleShowEquityIndicator, handleSummaryIndicator,
         handleEquityIndicator, handleShowCensusTracts,
         handleShowVD, handleShowPolls } from '../utils/AnalyticsTracking';
import Tabs from './Tabs';

// Data
import { selectVariable } from "../utils/Global";

export function ComponentGroup({title, children, className="", addPriorityIcon = false}: {title?: string, children: React.ReactNode, className?: string, addPriorityIcon?: boolean}):  JSX.Element {

    InfoIconListener();

    return(
        <div className={"ComponentGroup " + className}>
            <div className="Title">
                {title ?<h3>{title}</h3>: <></>}
                {addPriorityIcon?<div className="PriorityHighIcon"><PriorityHighIcon fontSize='small'/></div>: <></>}
            </div>
            {children}
        </div>
    )
}

export function PageDescription({geoFocus}: {geoFocus: string}):  JSX.Element {

    return(
        <ComponentGroup title="How to use VAM" className="PageDescription">
            {geoFocus === 'US' ? 
                <>
                    <p>The goal of the {appTitle} is to help voting rights advocates assess which communities could
                    benefit from additional access to polling locations.</p> 
                    <p><span className="">Get started by clicking an outlined state on the map.</span></p>
                </>: geoFocus === "State"? 
                    <>
                        <p>The mapping page shows an overview of how polling locations have changed over the last decade. Select a specific county to return a more detailed view.</p>
                    </> :
                    <>
                        <p>Hover over a specific county, or polling location to see move information.</p>
                    </>
            }
        </ComponentGroup>
    )
}

function SelectState({ selectedState, setSelectedState, setSelectedCounty, statesData, setGeoFocus } : 
                     { selectedState: State, setSelectedState: any, setSelectedCounty: any, statesData: GeoJSON.FeatureCollection, setGeoFocus: Function }) : JSX.Element {

    const handleChange = (event: SelectChangeEvent) => {
        const state = statesData.features.find((d:any) => d.properties!.stfp === event.target.value)!.properties as State;
        setSelectedState(state);
        handleClickState(state.name);
        setSelectedCounty(defaultCounty);
        setGeoFocus('State');
    };

    return (
        <div id="SelectState" className="QueryComponent">
            <FormControl fullWidth size="small">
                <InputLabel id="select-state-label">State</InputLabel>
                <Select
                labelId="select-state-label"
                id="select-state"
                value={selectedState.stfp}
                label="State"
                onChange={handleChange}
                >
                {statesData.features.map((state: GeoJSON.Feature) => (
                    <MenuItem key={state.properties!.stfp} value={state.properties!.stfp}>{state.properties!.name}</MenuItem>
                ))}
                </Select>
            </FormControl>
        </div>
    );
}

function SelectCounty({ selectedState, selectedCounty, setSelectedCounty, countiesData, setGeoFocus } : 
                      { selectedState: State, setSelectedState: any, selectedCounty: County, setSelectedCounty: any, countiesData: GeoJSON.FeatureCollection, setGeoFocus: Function} ) : JSX.Element {

    const allOpt = [{type: 'Feature', 
                    properties: {name: 'None'}, 
                    geometry: {} as GeoJSON.Geometry} as GeoJSON.Feature];

    return (
        <div id="SelectCounty" className="QueryComponent">
            <Autocomplete
            id="country-select-demo"
            fullWidth size="small"
            options={allOpt.concat(countiesData.features.filter((d: any) => d.properties.stfp === selectedState.stfp) as GeoJSON.Feature[])}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.properties?.name}
            onChange = {(_, feature) => {
                if (feature === null) {
                    return;
                } else if (feature?.properties?.name === 'None') {
                    setSelectedCounty(defaultCounty);
                    setGeoFocus('State');
                } else {
                    const properties = feature?.properties;
                    setSelectedCounty(properties as County);
                    handleClickCounty(properties?.cntyfp);
                    setGeoFocus('County');
                }
            }}
            renderOption={(props, option) => (
                <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                {option.properties?.name}
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                {...params}
                label={selectedCounty.cntyfp == ""? "County": selectedCounty.name }
                inputProps={{
                    ...params.inputProps,
                    autoComplete: 'new-password', // disable autocomplete and autofill
                }}
                />
            )}
            />
        </div>
    );
}

export function SelectGeography({ selectedState, setSelectedState, selectedCounty, setSelectedCounty, statesData, countiesData, loadedCountiesData, setGeoFocus } : 
                                { selectedState: State, setSelectedState: any, selectedCounty: County, setSelectedCounty: any, statesData: GeoJSON.FeatureCollection, 
                                    countiesData: GeoJSON.FeatureCollection, loadedCountiesData: boolean, setGeoFocus: Function }) : JSX.Element {

    return(
        <ComponentGroup>
            <Accordion defaultExpanded={true}>
                <AccordionSummary
                    expandIcon={<ArrowDropDownIcon />}
                    aria-controls="geography-content"
                    id="geography-header">
                    <h3>Select geography</h3>
                </AccordionSummary>
                <AccordionDetails>
                    <SelectState selectedState={selectedState} setSelectedState={setSelectedState} setSelectedCounty={setSelectedCounty} 
                    statesData={statesData} setGeoFocus={setGeoFocus}/>
                    {selectedState.stfp !== '' && loadedCountiesData? <SelectCounty selectedState={selectedState} setSelectedState={setSelectedState} 
                    selectedCounty={selectedCounty} setSelectedCounty={setSelectedCounty} countiesData={countiesData} setGeoFocus={setGeoFocus}/>: null}
                </AccordionDetails>
            </Accordion>
        </ComponentGroup>
    )
}

function SelectChangeYear({changeYear, setChangeYear, changeYearOpts} : {changeYear: ChangeYear, setChangeYear: any, changeYearOpts: ChangeYear[]}) : JSX.Element {

    const handleChange = (event: SelectChangeEvent) => {
        setChangeYear(changeYearOpts.find(d => d.id === event.target.value) as ChangeYear);
        handleClickYear(changeYearOpts.find(d => d.id === event.target.value)?.changeYear as string);
    };

    return(
        <ComponentGroup title="Select year" addPriorityIcon={true} className="ChangeYear">
            <div id="SelectChangeYear" className="QueryComponent">
                <FormControl fullWidth size="small">
                    <InputLabel id="select-change-year-label">Year</InputLabel>
                    <Select
                    labelId="select-change-year-label"
                    id="select-change-year"
                    value={changeYear.id}
                    label="county"
                    onChange={handleChange}
                    >
                    {changeYearOpts.map((changeYear: ChangeYear) => (
                        <MenuItem key={changeYear.id} value={changeYear.id}>{changeYear.descr}</MenuItem>
                    ))}
                    </Select>
                </FormControl>
            </div>
        </ComponentGroup>
    )
}


function SelectEquityIndicator({ equityIndicator, setEquityIndicator, showEquityIndicator, setShowEquityIndicator } : 
                               { equityIndicator: EquityIndicator, setEquityIndicator: any, showEquityIndicator: boolean, setShowEquityIndicator: Function }) : JSX.Element {

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedIndicator = selectVariable.equityIndicator.find(d => d.variable === (event.target as HTMLInputElement).value);
        setEquityIndicator(selectedIndicator as EquityIndicator);
        if (selectedIndicator) {
            handleEquityIndicator(selectedIndicator.descr);
        }
    };

    const disabled = !showEquityIndicator;

    return (
        <>
            <LayersSwitch toggle={showEquityIndicator} setToggle={setShowEquityIndicator} label="Show equity indicator" tracker={handleShowEquityIndicator}/>
                <FormGroup>
                    <RadioGroup
                        aria-labelledby="demo-radio-buttons-group-label"
                        name="radio-buttons-group"
                        value={equityIndicator.variable}
                        onChange={handleChange}
                    >
                        {selectVariable.equityIndicator.map((equityIndicator: EquityIndicator) => (
                            <FormControlLabel disabled={disabled} key={equityIndicator.variable} value={equityIndicator.variable} control={<Radio />} label={equityIndicator.descr}/>
                        ))}
                    </RadioGroup>
                </FormGroup>
        </>
    );
}

function SelectIndicator({setShowPolls, indicator, setIndicator}: 
                         {setShowPolls: Function, indicator: Indicator, setIndicator: Function}) {

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedIndicator = selectVariable.indicator.find(d => d.variable === (event.target as HTMLInputElement).value);
        setIndicator(selectedIndicator as Indicator);
        if (selectedIndicator) {
            handleSummaryIndicator(selectedIndicator.descr);
        }
    };

    return(
        <>
            <LayersSwitch disabled={true} toggle={true} setToggle={setShowPolls} label="Select indicator"/>
            <FormGroup>
                <RadioGroup
                    aria-labelledby="demo-radio-buttons-group-label"
                    name="radio-buttons-group"
                    value={indicator.variable}
                    onChange={handleChange}
                >
                    {selectVariable.indicator.map((indicator: Indicator) => (
                        <FormControlLabel disabled={false} key={indicator.variable} value={indicator.variable} control={<Radio />} label={IndicatorLabel(indicator)}/>
                    ))}
                </RadioGroup>
            </FormGroup>
        </>
    )
}

function LayersSwitch({ disabled=false, toggle, setToggle, label, tracker }: { disabled?: boolean, toggle: boolean, setToggle: Function, label: string, tracker?:  Function}) : JSX.Element {

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setToggle(event.target.checked);
        if (tracker) {
            tracker(event.target.checked);
        }
    };

    return (
        <FormControlLabel control={<Checkbox disabled={disabled} checked={toggle} onChange={handleChange} />} label={label} />
    );
}

export function LayersToggles({ geoFocus, equityIndicator, setEquityIndicator, showVD, setShowVD, showPolls, setShowPolls, 
                         showEquityIndicator, setShowEquityIndicator, showCensusTracts, setShowCensusTracts,
                         indicator, setIndicator } : 
                        {geoFocus: string, equityIndicator: EquityIndicator, setEquityIndicator: Function, 
                         showVD: boolean, setShowVD: Function, showPolls: boolean, setShowPolls: Function, 
                         showEquityIndicator: boolean, setShowEquityIndicator: Function, 
                         showCensusTracts: boolean, setShowCensusTracts: Function,
                         indicator: Indicator, setIndicator: Function}) : JSX.Element {

    return(
        <>
            { geoFocus === "County"? 
                <ComponentGroup>
                    <Accordion defaultExpanded={true}>
                        <AccordionSummary
                            expandIcon={<ArrowDropDownIcon />}
                            aria-controls="layers-content"
                            id="layers-header">
                            <h3>Show layers</h3>
                        </AccordionSummary>
                        <AccordionDetails>
                            <LayersSwitch toggle={showPolls} setToggle={setShowPolls} label="Show polls" tracker={handleShowPolls}/>
                            <LayersSwitch toggle={showCensusTracts} setToggle={setShowCensusTracts} label="Show census tracts" tracker={handleShowCensusTracts}/>
                            <LayersSwitch toggle={showVD} setToggle={setShowVD} label="Show voting districts 2020" tracker={handleShowVD}/>
                        </AccordionDetails>
                    </Accordion>
                </ComponentGroup>: 
                <ComponentGroup>
                    <Accordion defaultExpanded={true}>
                        <AccordionSummary
                            expandIcon={<ArrowDropDownIcon />}
                            aria-controls="indicator-content"
                            id="indicator-header">
                            <h3>Show summary indicators</h3>
                        </AccordionSummary>
                        <AccordionDetails>
                            <SelectIndicator setShowPolls={setShowPolls} indicator={indicator} setIndicator={setIndicator}/> 
                        </AccordionDetails>
                    </Accordion>
                </ComponentGroup>
            }
            
            <ComponentGroup >
                <Accordion defaultExpanded={true}>
                    <AccordionSummary
                        expandIcon={<ArrowDropDownIcon />}
                        aria-controls="equityindicator-content"
                        id="equityindicator-header">
                        <h3>Select equity indicator</h3>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SelectEquityIndicator equityIndicator={equityIndicator} setEquityIndicator={setEquityIndicator} showEquityIndicator={showEquityIndicator} setShowEquityIndicator={setShowEquityIndicator}/>
                    </AccordionDetails>
                </Accordion>
            </ComponentGroup>
        </>
    )
}

export function QueryMenu({ geoFocus, setGeoFocus, changeYear, setChangeYear, selectedState, setSelectedState, selectedCounty, 
                            setSelectedCounty, changeYearOpts, statesData, countiesData, 
                            equityIndicator, setEquityIndicator, showVD, setShowVD, showPolls, setShowPolls, showCensusTracts, setShowCensusTracts, showEquityIndicator, setShowEquityIndicator,
                            indicator, setIndicator, loadedCountiesData, 
                            pctBlackCVAPCountyFilter, setPctBlackCVAPCountyFilter, pctBlackCVAPRangeCountyMarks,
                            pctHispanicCVAPCountyFilter, setPctHispanicCVAPCountyFilter, pctHispanicCVAPRangeCountyMarks,
                            popDensityCountyFilter, setPopDensityCountyFilter, popDensityRangeCountyMarks,
                            changeNoPollsCountyFilter, setChangeYearCVAPPerPollCountyFilter, changeNoPollsRangeCountyMarks,
                            overallChangeCountyFilter, setOverallChangeCountyFilter, overallChangeRangeCountyMarks,
                            changeYearCVAPPerPollCountyFilter, setChangeNoPollsCountyFilter, changeYearCVAPPerPollCountyMarks,
                            baseYearCVAPPerPollCountyFilter, setBaseYearCVAPPerPollCountyFilter, baseYearCVAPPerPollCountyMarks,
                            pctBlackCVAPTractFilter, setPctBlackCVAPTractFilter, pctBlackCVAPRangeTractMarks,
                            pctHispanicCVAPTractFilter, setPctHispanicCVAPTractFilter, pctHispanicCVAPRangeTractMarks,
                            popDensityTractFilter, setPopDensityTractFilter, popDensityRangeTractMarks,
                            changeNoPollsTractFilter, setChangeNoPollsTractFilter, changeNoPollsRangeTractMarks,
                            overallChangeTractFilter, setOverallChangeTractFilter, overallChangeRangeTractMarks
                         } : 
                          { geoFocus: string, setGeoFocus: Function, changeYear: ChangeYear, setChangeYear: any, selectedState: State, setSelectedState: any, 
                            selectedCounty: County, setSelectedCounty: any, changeYearOpts: ChangeYear[], 
                            statesData: GeoJSON.FeatureCollection, countiesData: GeoJSON.FeatureCollection, 
                            equityIndicator: EquityIndicator, setEquityIndicator: Function, 
                            showVD: boolean, setShowVD: Function, showPolls: boolean, setShowPolls: Function, 
                            showCensusTracts: boolean, setShowCensusTracts: Function, 
                            showEquityIndicator: boolean, setShowEquityIndicator: Function,
                            indicator: Indicator, setIndicator: Function, loadedCountiesData: boolean,
                            pctBlackCVAPCountyFilter: number[], setPctBlackCVAPCountyFilter: Function, pctBlackCVAPRangeCountyMarks: any[],
                            pctHispanicCVAPCountyFilter: number[], setPctHispanicCVAPCountyFilter: Function, pctHispanicCVAPRangeCountyMarks: any[],
                            popDensityCountyFilter: number[], setPopDensityCountyFilter: Function, popDensityRangeCountyMarks: any[],
                            changeNoPollsCountyFilter: number[], setChangeYearCVAPPerPollCountyFilter: Function, changeNoPollsRangeCountyMarks: any[]
                            overallChangeCountyFilter: number[], setOverallChangeCountyFilter: Function, overallChangeRangeCountyMarks: any[],
                            changeYearCVAPPerPollCountyFilter: number[], setChangeNoPollsCountyFilter: Function, changeYearCVAPPerPollCountyMarks: any[],
                            baseYearCVAPPerPollCountyFilter: number[], setBaseYearCVAPPerPollCountyFilter: Function, baseYearCVAPPerPollCountyMarks: any[],
                            pctBlackCVAPTractFilter: number[], setPctBlackCVAPTractFilter: Function, pctBlackCVAPRangeTractMarks: any[],
                            pctHispanicCVAPTractFilter: number[], setPctHispanicCVAPTractFilter: Function, pctHispanicCVAPRangeTractMarks: any[],
                            popDensityTractFilter: number[], setPopDensityTractFilter: Function, popDensityRangeTractMarks: any[],
                            changeNoPollsTractFilter: number[], setChangeNoPollsTractFilter: Function, changeNoPollsRangeTractMarks: any[]
                            overallChangeTractFilter: number[], setOverallChangeTractFilter: Function, overallChangeRangeTractMarks: any[],}) {

    const [showPctBlackCVAPCountyFilter, setShowPctBlackCVAPCountyFilter] = useState(false);
    const [showPctHispanicCVAPCountyFilter, setShowPctHispanicCVAPCountyFilter] = useState(false);
    const [showPopDensityCountyFilter, setShowPopDensityCountyFilter] = useState(false);

    const [showChangeNoPollsCountyFilter, setShowChangeNoPollsCountyFilter] = useState(false);
    const [showOverallChangeCountyFilter, setShowOverallChangeCountyFilter] = useState(false);
    const [showChangeYearCVAPPerPollCountyFilter, setShowChangeYearCVAPPerPollCountyFilter] = useState(false);
    const [showBaseYearCVAPPerPollCountyFilter, setShowBaseYearCVAPPerPollCountyFilter] = useState(false);
    
    const [showPctBlackCVAPTractFilter, setShowPctBlackCVAPTractFilter] = useState(false);
    const [showPctHispanicCVAPTractFilter, setShowPctHispanicCVAPTractFilter] = useState(false);
    const [showPopDensityTractFilter, setShowPopDensityTractFilter] = useState(false);

    const [showChangeNoPollsTractFilter, setShowChangeNoPollsTractFilter] = useState(false);
    const [showOverallChangeTractFilter, setShowOverallChangeTractFilter] = useState(false);

    return(
        <div className="Query">
            <div className="QueryStyled">
                    <SelectChangeYear changeYear={changeYear} setChangeYear={setChangeYear} changeYearOpts={changeYearOpts} />
                    <Tabs geoFocus={geoFocus} setGeoFocus={setGeoFocus} 
                        selectedState={selectedState} setSelectedState={setSelectedState} 
                        selectedCounty={selectedCounty} setSelectedCounty={setSelectedCounty} 
                        statesData={statesData}
                        countiesData={countiesData}
                        equityIndicator={equityIndicator} setEquityIndicator={setEquityIndicator}
                        showVD={showVD} setShowVD={setShowVD} showPolls={showPolls} setShowPolls={setShowPolls} 
                        showCensusTracts={showCensusTracts} setShowCensusTracts={setShowCensusTracts} 
                        showEquityIndicator={showEquityIndicator} setShowEquityIndicator={setShowEquityIndicator}
                        indicator={indicator} setIndicator={setIndicator} loadedCountiesData={loadedCountiesData}
                        pctBlackCVAPCountyFilter={pctBlackCVAPCountyFilter} setPctBlackCVAPCountyFilter={setPctBlackCVAPCountyFilter} pctBlackCVAPRangeCountyMarks={pctBlackCVAPRangeCountyMarks}
                        showPctBlackCVAPCountyFilter={showPctBlackCVAPCountyFilter} setShowPctBlackCVAPCountyFilter={setShowPctBlackCVAPCountyFilter}
                        pctHispanicCVAPCountyFilter={pctHispanicCVAPCountyFilter} setPctHispanicCVAPCountyFilter={setPctHispanicCVAPCountyFilter} pctHispanicCVAPRangeCountyMarks={pctHispanicCVAPRangeCountyMarks}
                        showPctHispanicCVAPCountyFilter={showPctHispanicCVAPCountyFilter} setShowPctHispanicCVAPCountyFilter={setShowPctHispanicCVAPCountyFilter}
                        popDensityCountyFilter={popDensityCountyFilter} setPopDensityCountyFilter={setPopDensityCountyFilter} popDensityRangeCountyMarks={popDensityRangeCountyMarks}
                        showPopDensityCountyFilter={showPopDensityCountyFilter} setShowPopDensityCountyFilter={setShowPopDensityCountyFilter}

                        changeNoPollsCountyFilter={changeNoPollsCountyFilter} setChangeNoPollsCountyFilter={setChangeNoPollsCountyFilter}  
                        changeNoPollsRangeCountyMarks={changeNoPollsRangeCountyMarks} showChangeNoPollsCountyFilter={showChangeNoPollsCountyFilter} setShowChangeNoPollsCountyFilter={setShowChangeNoPollsCountyFilter}
                        overallChangeCountyFilter={overallChangeCountyFilter} setOverallChangeCountyFilter={setOverallChangeCountyFilter} 
                        overallChangeRangeCountyMarks={overallChangeRangeCountyMarks} showOverallChangeCountyFilter={showOverallChangeCountyFilter} setShowOverallChangeCountyFilter={setShowOverallChangeCountyFilter}
                        changeYearCVAPPerPollCountyFilter={changeYearCVAPPerPollCountyFilter} setChangeYearCVAPPerPollCountyFilter={setChangeYearCVAPPerPollCountyFilter} 
                        changeYearCVAPPerPollCountyMarks={changeYearCVAPPerPollCountyMarks} showChangeYearCVAPPerPollCountyFilter={showChangeYearCVAPPerPollCountyFilter} setShowChangeYearCVAPPerPollCountyFilter={setShowChangeYearCVAPPerPollCountyFilter}
                        baseYearCVAPPerPollCountyFilter={baseYearCVAPPerPollCountyFilter} setBaseYearCVAPPerPollCountyFilter={setBaseYearCVAPPerPollCountyFilter} 
                        baseYearCVAPPerPollCountyMarks={baseYearCVAPPerPollCountyMarks} showBaseYearCVAPPerPollCountyFilter={showBaseYearCVAPPerPollCountyFilter} setShowBaseYearCVAPPerPollCountyFilter={setShowBaseYearCVAPPerPollCountyFilter}

                        pctBlackCVAPTractFilter={pctBlackCVAPTractFilter} setPctBlackCVAPTractFilter={setPctBlackCVAPTractFilter} pctBlackCVAPRangeTractMarks={pctBlackCVAPRangeTractMarks}
                        showPctBlackCVAPTractFilter={showPctBlackCVAPTractFilter} setShowPctBlackCVAPTractFilter={setShowPctBlackCVAPTractFilter}
                        pctHispanicCVAPTractFilter={pctHispanicCVAPTractFilter} setPctHispanicCVAPTractFilter={setPctHispanicCVAPTractFilter} pctHispanicCVAPRangeTractMarks={pctHispanicCVAPRangeTractMarks}
                        showPctHispanicCVAPTractFilter={showPctHispanicCVAPTractFilter} setShowPctHispanicCVAPTractFilter={setShowPctHispanicCVAPTractFilter}
                        popDensityTractFilter={popDensityTractFilter} setPopDensityTractFilter={setPopDensityTractFilter} popDensityRangeTractMarks={popDensityRangeTractMarks}
                        showPopDensityTractFilter={showPopDensityTractFilter} setShowPopDensityTractFilter={setShowPopDensityTractFilter}

                        changeNoPollsTractFilter={changeNoPollsTractFilter} setChangeNoPollsTractFilter={setChangeNoPollsTractFilter}  
                        changeNoPollsRangeTractMarks={changeNoPollsRangeTractMarks} showChangeNoPollsTractFilter={showChangeNoPollsTractFilter} setShowChangeNoPollsTractFilter={setShowChangeNoPollsTractFilter}
                        overallChangeTractFilter={overallChangeTractFilter} setOverallChangeTractFilter={setOverallChangeTractFilter} 
                        overallChangeRangeTractMarks={overallChangeRangeTractMarks} showOverallChangeTractFilter={showOverallChangeTractFilter} setShowOverallChangeTractFilter={setShowOverallChangeTractFilter}

                    />
                </div>
        </div>
    );
}
